import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { Box, Chip, Tab, Tabs, Typography } from "@mui/material";
import {
  History as HistoryIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import {
  PtiComplianceListItemDto,
  PtiComplianceParams,
  useGetPtiComplianceQuery,
  useGetPtiComplianceSummaryQuery,
} from "../../../api/ptiApi";
import { useGetTrailersQuery } from "../../../api/trailersApi";
import { useGetUsersPaginatedWithSearchQuery } from "../../../api/usersApi";
import { useGetVehiclesQuery } from "../../../api/vehiclesApi";
import { DEFAULT_DATA_TABLE_PAGE_SIZE } from "../../../constants/dataTable";
import { UserRoles } from "../../../constants/roles";
import {
  Action,
  Column,
  DataTable,
  createColumn,
  makeColumnsSearchable,
} from "../../../components/common/DataTable/DataTable";
import { SearchableColumn } from "../../../components/common/SearchInputs";
import { useDataTablePagination } from "../../../hooks/useDataTablePagination";
import { PaginatedQueryParams } from "../../../types/pagination";
import {
  getPtiComplianceStatusLabel,
  getPtiStatusLabel,
  PTI_COMPLIANCE_STATUS_FILTER_OPTIONS,
} from "../ptiConstants";

const ENTITY_TYPE_OPTIONS: {
  value: string | null;
  tabValue: string;
  label: string;
}[] = [
  { value: null, tabValue: "all", label: "All" },
  { value: "Vehicle", tabValue: "Vehicle", label: "Vehicles" },
  { value: "Trailer", tabValue: "Trailer", label: "Trailers" },
];

const buildComplianceQueryArg = (
  queryParams: PaginatedQueryParams,
): PtiComplianceParams => {
  const filters = queryParams.filters ?? {};

  return {
    pageNumber: queryParams.pageNumber,
    pageSize: queryParams.pageSize,
    assetType: filters.assetType as string | undefined,
    status: (filters.status as string | undefined) ?? "needsAttention",
    driverUserId: filters.driverUserId
      ? Number(filters.driverUserId)
      : undefined,
    vehicleId: filters.vehicleId ? Number(filters.vehicleId) : undefined,
    trailerId: filters.trailerId ? Number(filters.trailerId) : undefined,
    search: filters.search as string | undefined,
  };
};

const getComplianceChip = (row: PtiComplianceListItemDto) => {
  switch (row.complianceStatus) {
    case "Overdue":
      return (
        <Chip
          label={
            row.daysOverdue > 0 ? `${row.daysOverdue}d overdue` : "Overdue"
          }
          size="small"
          color="error"
          variant="filled"
          sx={{ fontSize: "0.75rem" }}
        />
      );
    case "DueSoon":
      return (
        <Chip
          label={
            row.hoursUntilDue <= 24
              ? `${row.hoursUntilDue}h left`
              : `${Math.ceil(row.hoursUntilDue / 24)}d left`
          }
          size="small"
          color="warning"
          variant="outlined"
          sx={{ fontSize: "0.75rem" }}
        />
      );
    default:
      return (
        <Chip
          label={getPtiComplianceStatusLabel(row.complianceStatus)}
          size="small"
          color="success"
          variant="outlined"
          sx={{ fontSize: "0.75rem" }}
        />
      );
  }
};

interface PtiComplianceListProps {
  initialStatus?: string;
  initialDriverUserId?: number;
  initialVehicleId?: number;
  initialTrailerId?: number;
  initialInspectionId?: number;
}

const PtiComplianceList: React.FC<PtiComplianceListProps> = ({
  initialStatus,
  initialDriverUserId,
  initialVehicleId,
  initialTrailerId,
  initialInspectionId,
}) => {
  const navigate = useNavigate();
  const [selectedInspectionId, setSelectedInspectionId] = useState<
    number | null
  >(initialInspectionId ?? null);
  const [entityTypeFilter, setEntityTypeFilter] = useState<string | null>(
    initialVehicleId ? "Vehicle" : initialTrailerId ? "Trailer" : null,
  );
  const [tableFilters, setTableFilters] = useState<Record<string, any>>({
    status: initialStatus ?? "needsAttention",
    ...(initialDriverUserId ? { driverUserId: initialDriverUserId } : {}),
    ...(initialVehicleId ? { vehicleId: initialVehicleId } : {}),
    ...(initialTrailerId ? { trailerId: initialTrailerId } : {}),
  });

  const { data: vehicles = [] } = useGetVehiclesQuery();
  const { data: trailers = [] } = useGetTrailersQuery();
  const { data: driversData } = useGetUsersPaginatedWithSearchQuery({
    pageNumber: 1,
    pageSize: 500,
    filters: { role: UserRoles.TRUCK_DRIVER },
  });

  const vehicleOptions = vehicles
    .map((vehicle) => {
      const unit = vehicle.registrationNumber;
      const label =
        unit && vehicle.name
          ? `${unit} · ${vehicle.name}`
          : unit || vehicle.name || `Vehicle ${vehicle.id}`;
      return { value: vehicle.id, label };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  const trailerOptions = trailers
    .filter((trailer) => !trailer.isArchived)
    .map((trailer) => ({
      value: trailer.id,
      label: trailer.unitNumber || `Trailer ${trailer.id}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const driverOptions =
    driversData?.items.map((driver) => ({
      value: driver.id,
      label: `${driver.firstName} ${driver.lastName}`.trim(),
    })) ?? [];

  const buildFilters = useCallback(
    (
      nextTableFilters: Record<string, any>,
      nextEntityType: string | null,
    ): Record<string, any> => ({
      ...nextTableFilters,
      ...(nextEntityType ? { assetType: nextEntityType } : {}),
    }),
    [],
  );

  const {
    items: complianceItems,
    isLoading,
    isLoadingRows,
    isLoadingMore,
    totalCount,
    hasNextPage,
    handleSearchChange,
    handleLoadMore,
  } = useDataTablePagination<PtiComplianceListItemDto, PtiComplianceParams>(
    useGetPtiComplianceQuery,
    DEFAULT_DATA_TABLE_PAGE_SIZE,
    { buildQueryArg: buildComplianceQueryArg },
  );

  useLayoutEffect(() => {
    const filters: Record<string, any> = {
      status: initialStatus ?? "needsAttention",
    };
    if (initialDriverUserId) filters.driverUserId = initialDriverUserId;
    if (initialVehicleId) filters.vehicleId = initialVehicleId;
    if (initialTrailerId) filters.trailerId = initialTrailerId;
    if (Object.keys(filters).length > 0) {
      handleSearchChange(
        buildFilters(
          filters,
          initialVehicleId ? "Vehicle" : initialTrailerId ? "Trailer" : null,
        ),
      );
    }
    // Deep-link from the Fleet hub should apply once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summaryFilters = useMemo(
    () => buildFilters(tableFilters, entityTypeFilter),
    [buildFilters, entityTypeFilter, tableFilters],
  );

  const { data: summary } = useGetPtiComplianceSummaryQuery({
    assetType: summaryFilters.assetType,
    driverUserId: summaryFilters.driverUserId
      ? Number(summaryFilters.driverUserId)
      : undefined,
    vehicleId: summaryFilters.vehicleId
      ? Number(summaryFilters.vehicleId)
      : undefined,
    trailerId: summaryFilters.trailerId
      ? Number(summaryFilters.trailerId)
      : undefined,
    search: summaryFilters.search,
  });

  const handleTableSearchChange = useCallback(
    (nextTableFilters: Record<string, any>) => {
      setTableFilters(nextTableFilters);
      handleSearchChange(buildFilters(nextTableFilters, entityTypeFilter));
    },
    [buildFilters, entityTypeFilter, handleSearchChange],
  );

  const handleEntityTypeChange = useCallback(
    (nextEntityType: string | null) => {
      setEntityTypeFilter(nextEntityType);
      handleSearchChange(buildFilters(tableFilters, nextEntityType));
    },
    [buildFilters, handleSearchChange, tableFilters],
  );

  const handleSummaryChipClick = useCallback(
    (status: string) => {
      const nextFilters = { ...tableFilters, status };
      setTableFilters(nextFilters);
      handleSearchChange(buildFilters(nextFilters, entityTypeFilter));
    },
    [buildFilters, entityTypeFilter, handleSearchChange, tableFilters],
  );

  const columns: Column[] = useMemo(
    () => [
      createColumn("driverName", "Driver"),
      createColumn("assetType", "Asset Type"),
      createColumn("assetDisplayLabel", "Asset"),
      createColumn("assignmentStartDate", "Assignment Start", {
        renderCell: (value: string) => format(new Date(value), "MMM d, yyyy"),
      }),
      createColumn("lastPtiDate", "Last PTI", {
        renderCell: (_value: unknown, row: PtiComplianceListItemDto) =>
          row.lastPtiDate ? (
            <Box>
              <Typography sx={{ fontSize: "0.8125rem" }}>
                {format(new Date(row.lastPtiDate), "MMM d, yyyy")}
              </Typography>
              {row.lastPtiStatus ? (
                <Typography sx={{ fontSize: "0.75rem" }} color="text.secondary">
                  {getPtiStatusLabel(row.lastPtiStatus)}
                </Typography>
              ) : null}
            </Box>
          ) : (
            "—"
          ),
      }),
      createColumn("dueAt", "Due Date", {
        renderCell: (value: string) =>
          format(new Date(value), "MMM d, yyyy h:mm a"),
      }),
      createColumn("complianceStatus", "Compliance", {
        renderCell: (_value: unknown, row: PtiComplianceListItemDto) =>
          getComplianceChip(row),
      }),
    ],
    [],
  );

  const searchableColumns: SearchableColumn[] = useMemo(
    () => [
      ...makeColumnsSearchable(columns, ["driverName", "assetDisplayLabel"], {
        driverName: "string",
        assetDisplayLabel: "string",
      }),
      {
        field: "driverUserId",
        headerName: "Driver",
        searchable: true,
        searchType: "select",
        selectOptions: driverOptions,
      },
      {
        field: "vehicleId",
        headerName: "Vehicle",
        searchable: true,
        searchType: "select",
        selectOptions: vehicleOptions,
      },
      {
        field: "trailerId",
        headerName: "Trailer",
        searchable: true,
        searchType: "select",
        selectOptions: trailerOptions,
      },
      {
        field: "status",
        headerName: "Compliance",
        searchable: true,
        searchType: "select",
        selectOptions: PTI_COMPLIANCE_STATUS_FILTER_OPTIONS,
      },
    ],
    [columns, driverOptions, trailerOptions, vehicleOptions],
  );

  const getActions = (
    row: PtiComplianceListItemDto,
  ): Action<PtiComplianceListItemDto>[] => {
    const actions: Action<PtiComplianceListItemDto>[] = [];

    if (row.lastPtiId) {
      actions.push({
        icon: <ViewIcon fontSize="small" />,
        label: "View latest PTI",
        onClick: () => navigate(`/pti/${row.lastPtiId}`),
        color: "primary",
      });
    }

    actions.push({
      icon: <HistoryIcon fontSize="small" />,
      label: "Open asset",
      onClick: () => {
        if (row.assetType === "Vehicle") {
          navigate(`/vehicles/${row.assetId}/edit`);
        } else {
          navigate(`/trailers/${row.assetId}/edit`);
        }
      },
      color: "primary",
    });

    return actions;
  };

  const renderSummaryChips = () => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, px: 1, pb: 1 }}>
      <Chip
        label={`Overdue: ${summary?.overdueCount ?? 0}`}
        color="error"
        variant={tableFilters.status === "overdue" ? "filled" : "outlined"}
        onClick={() => handleSummaryChipClick("overdue")}
        sx={{ cursor: "pointer" }}
      />
      <Chip
        label={`Due soon: ${summary?.dueSoonCount ?? 0}`}
        color="warning"
        variant={tableFilters.status === "dueSoon" ? "filled" : "outlined"}
        onClick={() => handleSummaryChipClick("dueSoon")}
        sx={{ cursor: "pointer" }}
      />
      <Chip
        label={`Compliant: ${summary?.compliantCount ?? 0}`}
        color="success"
        variant={tableFilters.status === "compliant" ? "filled" : "outlined"}
        onClick={() => handleSummaryChipClick("compliant")}
        sx={{ cursor: "pointer" }}
      />
      <Chip
        label={`Needs attention: ${(summary?.overdueCount ?? 0) + (summary?.dueSoonCount ?? 0)}`}
        color="default"
        variant={
          tableFilters.status === "needsAttention" ? "filled" : "outlined"
        }
        onClick={() => handleSummaryChipClick("needsAttention")}
        sx={{ cursor: "pointer" }}
      />
    </Box>
  );

  const renderEntityTypeToolbar = () => (
    <Box sx={{ px: 1 }}>
      {renderSummaryChips()}
      <Tabs
        value={entityTypeFilter ?? "all"}
        onChange={(_event, nextValue: string) =>
          handleEntityTypeChange(nextValue === "all" ? null : nextValue)
        }
        aria-label="PTI compliance asset type tabs"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ minHeight: 44 }}
      >
        {ENTITY_TYPE_OPTIONS.map((option) => (
          <Tab
            key={option.tabValue}
            value={option.tabValue}
            label={option.label}
            sx={{ minHeight: 44, py: 0.75 }}
          />
        ))}
      </Tabs>
    </Box>
  );

  return (
    <>
      <DataTable<PtiComplianceListItemDto>
        items={complianceItems}
        columns={columns}
        actions={getActions}
        isLoading={isLoading}
        isLoadingRows={isLoadingRows}
        totalCount={totalCount}
        hasNextPage={hasNextPage}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
        getRowId={(row) =>
          `${row.driverUserId}-${row.assetType}-${row.assetId}`
        }
        toolbar={renderEntityTypeToolbar()}
        enableSearch
        searchableColumns={searchableColumns}
        onSearchChange={handleTableSearchChange}
      />
    </>
  );
};

export default PtiComplianceList;
